import { Link } from 'wouter';
import { usePageTitle } from '@/hooks/usePageTitle';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import TiltCard from '@/components/TiltCard';
import AnimatedCounter from '@/components/AnimatedCounter';
import NorthgateLaptopStack from '@/components/NorthgateLaptopStack';
import { ScrollProgress, useReveal } from '@/components/ScrollProgress';
import { useLanguage } from '@/contexts/LanguageContext';

const services = [
  { icon: '01', titleKey: 'svc1_title', descKey: 'svc1_desc', href: '/services' },
  { icon: '02', titleKey: 'svc2_title', descKey: 'svc2_desc', href: '/services' },
  { icon: '03', titleKey: 'svc3_title', descKey: 'svc3_desc', href: '/services' },
];

const trust = [
  { number: '01', titleKey: 'trust1_title', descKey: 'trust1_desc' },
  { number: '02', titleKey: 'trust2_title', descKey: 'trust2_desc' },
  { number: '03', titleKey: 'trust3_title', descKey: 'trust3_desc' },
  { number: '04', titleKey: 'trust4_title', descKey: 'trust4_desc' },
];

const workItems = [
  {
    name: 'Cafe & Bar Luxor',
    desc: 'Real client — bilingual website (DE/EN) for a cafe & bar in Solingen. Speisekarte, gallery, table reservation, and DSGVO compliance.',
    tag: 'Real Client',
    image: '/manus-storage/luxor_screenshot_9c25a67e.jpg',
    url: 'https://cafebarluxor.com',
  },
  {
    name: 'Villa Dorada Resort',
    desc: 'Luxury resort & spa website with booking, room showcase, restaurant, and experiences — Costa del Sol.',
    tag: 'Website',
    image: '/manus-storage/portfolio-villadorada_new_a0932779.webp',
    url: 'https://www.villadorada.xyz',
  },
  {
    name: 'Peak Auto Detailing',
    desc: 'High-converting landing page for a premium auto detailing business. Services, pricing, and instant booking.',
    tag: 'Landing Page',
    image: '/manus-storage/portfolio-peakdetail_new_710cd381.webp',
    url: 'https://www.peakdetail.xyz',
  },
  {
    name: 'Müller & Partner',
    desc: 'Professional tax consulting website. Services, team, and client portal. Built for Germany.',
    tag: 'Website',
    image: '/manus-storage/portfolio-muellerstb_new_c91e0d85.webp',
    url: 'https://www.muellerstb.xyz',
  },
];

export default function Home() {
  usePageTitle('Web Design Agency Germany | Northgate Web');
  const { t } = useLanguage();
  useReveal();

  return (
    <main className="ng-home">
      <ScrollProgress />
      <Navbar />

      <section className="ng-hero">
        <div className="container ng-hero__grid">
          <div className="ng-hero__copy">
            <div className="eyebrow reveal">{t('hero_eyebrow')}</div>
            <h1>
              <span className="reveal reveal-delay-1">{t('hero_title_1')}</span>
              <span className="reveal reveal-delay-2">{t('hero_title_2')}</span>
              <span className="reveal reveal-delay-3 gradient-text">{t('hero_title_3')}</span>
            </h1>
            <p className="ng-hero__description reveal reveal-delay-4">{t('hero_sub')}</p>
            <div className="ng-hero__actions reveal reveal-delay-5">
              <Link href="/contact" className="btn-primary">
                <span>{t('hero_cta1')}</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <Link href="/work" className="btn-secondary">{t('hero_cta2')}</Link>
            </div>
          </div>
          <div className="ng-hero__showcase reveal reveal-delay-2">
            <NorthgateLaptopStack />
          </div>
        </div>
      </section>

      <section className="ng-section ng-section--services">
        <div className="container">
          <div className="ng-section__heading ng-section__heading--split">
            <div className="reveal">
              <div className="eyebrow">{t('services_eyebrow')}</div>
              <h2>{t('services_title')}</h2>
            </div>
            <p className="ng-section__intro reveal reveal-delay-1">{t('hero_sub')}</p>
          </div>
          <div className="ng-services-layout">
            <div className="ng-service-grid">
              {services.map((service, index) => (
                <TiltCard key={service.titleKey} className={`ng-service-card reveal reveal-delay-${Math.min(index + 1, 3)}`}>
                  <span className="ng-service-card__number">{service.icon}</span>
                  <h3>{t(service.titleKey)}</h3>
                  <p>{t(service.descKey)}</p>
                  <Link href={service.href} className="ng-text-link" title={`Learn more about ${t(service.titleKey)}`}>
                    {t('learn_more')} <span aria-hidden="true">↗</span>
                  </Link>
                </TiltCard>
              ))}
            </div>
            <div className="ng-services-layout__visual reveal reveal-delay-2">
              <NorthgateLaptopStack variant="inline" />
            </div>
          </div>
        </div>
      </section>

      <section className="ng-section ng-section--quiet">
        <div className="container">
          <div className="ng-section__heading reveal">
            <div className="eyebrow">{t('why_eyebrow')}</div>
            <h2>{t('why_title')}</h2>
          </div>
          <div className="ng-trust-grid">
            {trust.map((item, index) => (
              <article key={item.titleKey} className={`ng-trust-card reveal reveal-delay-${Math.min(index + 1, 4)}`}>
                <span>{item.number}</span>
                <div>
                  <h3>{t(item.titleKey)}</h3>
                  <p>{t(item.descKey)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ng-stat-band">
        <div className="container ng-stat-band__grid">
          {[
            { target: 7, suffix: '', labelKey: 'stat2_label' },
            { target: 0, suffix: '', labelKey: 'stat1_label', hideNumber: true },
            { target: 4, suffix: '', labelKey: 'stat3_label' },
          ].map((stat, index) => (
            <div key={stat.labelKey} className={`ng-stat reveal reveal-delay-${index + 1}`}>
              <div className="ng-stat__number">{!stat.hideNumber && <AnimatedCounter target={stat.target} suffix={stat.suffix} />}</div>
              <p>{t(stat.labelKey)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ng-section ng-section--work">
        <div className="container">
          <div className="ng-section__heading ng-section__heading--split">
            <div className="reveal">
              <div className="eyebrow">OUR WORK</div>
              <h2>Real sites. Real results.</h2>
            </div>
            <Link href="/work" className="btn-secondary reveal reveal-delay-1">{t('see_all')} Work</Link>
          </div>
          <div className="ng-work-grid">
            {workItems.map((item, index) => (
              <a key={item.name} href={item.url} target="_blank" rel="noreferrer" className={`ng-project-card reveal reveal-delay-${Math.min(index + 1, 4)}`}>
                <div className="ng-project-card__laptop">
                  <div className="ng-project-card__screen">
                    <img src={item.image} alt={item.name} loading="lazy" decoding="async" />
                  </div>
                  <div className="ng-project-card__base" />
                </div>
                <div className="ng-project-card__content">
                  <span>{item.tag}</span>
                  <h3>{item.name}</h3>
                  <p>{item.desc}</p>
                  <strong>View Live Site <i aria-hidden="true">↗</i></strong>
                </div>
              </a>
            ))}
          </div>
          <div className="ng-work-cta reveal">
            <Link href="/work" className="btn-primary"><span>{t('see_all')}</span></Link>
          </div>
        </div>
      </section>

      <section className="ng-conversion">
        <div className="container ng-conversion__inner reveal">
          <div>
            <div className="eyebrow">NORTHGATE WEB</div>
            <h2>{t('strip_title')}</h2>
          </div>
          <Link href="/contact" className="btn-primary">
            <span>{t('strip_cta')}</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
